import React, { useEffect, useState } from "react";
import axiosInstance from "../config/axiosConfig";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { REGLA_TIPOS_COBRANZA, TIPOS_CORREO_COBRANZA, VARIABLES_CORREO_COBRANZA } from "../utils/variablesCorreoCobranza";

const TEMPLATE_VACIO = {
    id: null,
    nombre: "",
    tipo: "",
    asunto: "",
    cuerpo: "",
    activo: 1,
};

const variablesDisponibles = VARIABLES_CORREO_COBRANZA;

const CorreoTemplates = () => {
    const [templates, setTemplates] = useState([]);
    const [templateActual, setTemplateActual] = useState(TEMPLATE_VACIO);
    const [loading, setLoading] = useState(false);

    const cargarTemplates = async () => {
        try {
            setLoading(true);
            const { data } = await axiosInstance.get("/administracion/correo-templates");
            setTemplates(data.data || []);
        } catch (error) {
            console.error("Error al cargar templates de correo", error);
            alert("No se pudieron cargar los templates de correo.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        cargarTemplates();
    }, []);

    const limpiarFormulario = () => {
        setTemplateActual(TEMPLATE_VACIO);
    };

    const editarTemplate = (template) => {
        setTemplateActual({
            id: template.id,
            nombre: template.nombre || "",
            tipo: template.tipo || "",
            asunto: template.asunto || "",
            cuerpo: template.cuerpo || "",
            activo: Number(template.activo) === 1 ? 1 : 0,
        });
    };

    const insertarVariable = (variable) => {
        setTemplateActual((prev) => ({
            ...prev,
            cuerpo: `${prev.cuerpo || ""}${prev.cuerpo ? " " : ""}${variable}`,
        }));
    };

    const guardarTemplate = async () => {
        if (!templateActual.nombre.trim()) {
            alert("Ingresa un nombre para el template.");
            return;
        }

        if (!templateActual.cuerpo.trim()) {
            alert("Ingresa el mensaje del template.");
            return;
        }

        try {
            setLoading(true);
            await axiosInstance.post("/administracion/correo-templates/guardar", {
                id: templateActual.id,
                nombre: templateActual.nombre,
                tipo: templateActual.tipo,
                asunto: templateActual.asunto,
                cuerpo: templateActual.cuerpo,
                activo: templateActual.activo,
            });
            await cargarTemplates();
            limpiarFormulario();
        } catch (error) {
            console.error("Error al guardar template de correo", error);
            alert("No se pudo guardar el template.");
        } finally {
            setLoading(false);
        }
    };

    const eliminarTemplate = async (template) => {
        if (!window.confirm(`Eliminar template "${template.nombre}"?`)) return;

        try {
            setLoading(true);
            await axiosInstance.post("/administracion/correo-templates/eliminar", { id: template.id });
            await cargarTemplates();
            if (templateActual.id === template.id) limpiarFormulario();
        } catch (error) {
            console.error("Error al eliminar template de correo", error);
            alert("No se pudo eliminar el template.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="container mt-4">
            <div className="card shadow">
                <div className="card-header bg-primary text-white d-flex justify-content-between align-items-center">
                    <h4 className="mb-0">Templates de Correo</h4>
                    <button type="button" className="btn btn-light btn-sm" onClick={limpiarFormulario} disabled={loading}>
                        Nuevo
                    </button>
                </div>
                <div className="card-body">
                    <div className="row g-4">
                        <div className="col-lg-5">
                            <div className="mb-3">
                                <label className="form-label fw-bold">Nombre</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={templateActual.nombre}
                                    onChange={(e) => setTemplateActual((prev) => ({ ...prev, nombre: e.target.value }))}
                                    disabled={loading}
                                    placeholder="Ej: Cobranza mensual"
                                />
                            </div>

                            <div className="row g-2 mb-3">
                                <div className="col-sm-5">
                                    <label className="form-label fw-bold">Tipo de envío</label>
                                    <select
                                        className="form-select"
                                        value={templateActual.tipo}
                                        onChange={(e) => setTemplateActual((prev) => ({ ...prev, tipo: e.target.value }))}
                                        disabled={loading}
                                    >
                                        <option value="">Sin tipo (solo a mano)</option>
                                        {TIPOS_CORREO_COBRANZA.map((t) => <option key={t.clave} value={t.clave}>{t.label}</option>)}
                                    </select>
                                </div>
                                <div className="col-sm-7">
                                    <label className="form-label fw-bold">Asunto</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={templateActual.asunto}
                                        onChange={(e) => setTemplateActual((prev) => ({ ...prev, asunto: e.target.value }))}
                                        disabled={loading}
                                        placeholder="Vacío = asunto por defecto del tipo"
                                    />
                                </div>
                                <div className="col-12 form-text mt-0">
                                    El envío masivo de cobranza usa el template activo del tipo de cada paciente.
                                    {" "}{REGLA_TIPOS_COBRANZA}
                                </div>
                            </div>

                            <div className="mb-3">
                                <label className="form-label fw-bold">Variables</label>
                                <div className="d-flex flex-wrap gap-2">
                                    {variablesDisponibles.map((variable) => (
                                        <button
                                            type="button"
                                            key={variable.clave}
                                            className="btn btn-outline-secondary btn-sm"
                                            onClick={() => insertarVariable(`{${variable.clave}}`)}
                                            disabled={loading}
                                            title={variable.descripcion}
                                        >
                                            {`{${variable.clave}}`}
                                        </button>
                                    ))}
                                </div>
                                <div className="form-text">
                                    Montos con formato $1.234.567. <strong>Un párrafo que usa un monto en $0 no sale</strong>:
                                    deja cada monto en su propia línea (ej. "Deuda anterior: {"{saldo_anterior}"}") para no perder el resto del texto.
                                    Las de reajuste (solo valor turno) quedan vacías si el paciente no tiene reajuste programado: en el envío
                                    masivo ese paciente no se notifica con ese template.
                                </div>
                            </div>

                            <div className="mb-3">
                                <label className="form-label fw-bold">Mensaje</label>
                                <div style={{ backgroundColor: "#fff", borderRadius: "4px" }}>
                                    <ReactQuill
                                        theme="snow"
                                        value={templateActual.cuerpo}
                                        onChange={(value) => setTemplateActual((prev) => ({ ...prev, cuerpo: value }))}
                                        readOnly={loading}
                                        modules={{
                                            toolbar: [
                                                ["bold", "italic", "underline", "strike"],
                                                [{ list: "ordered" }, { list: "bullet" }],
                                                [{ color: [] }, { background: [] }],
                                            ]
                                        }}
                                        style={{ height: "220px", marginBottom: "50px" }}
                                    />
                                </div>
                            </div>

                            <div className="form-check form-switch mb-3">
                                <input
                                    className="form-check-input"
                                    type="checkbox"
                                    id="templateActivo"
                                    checked={Number(templateActual.activo) === 1}
                                    onChange={(e) => setTemplateActual((prev) => ({ ...prev, activo: e.target.checked ? 1 : 0 }))}
                                    disabled={loading}
                                />
                                <label className="form-check-label" htmlFor="templateActivo">Activo</label>
                            </div>

                            <div className="d-flex justify-content-end gap-2">
                                <button type="button" className="btn btn-secondary" onClick={limpiarFormulario} disabled={loading}>
                                    Limpiar
                                </button>
                                <button type="button" className="btn btn-success" onClick={guardarTemplate} disabled={loading}>
                                    Guardar
                                </button>
                            </div>
                        </div>

                        <div className="col-lg-7">
                            <div className="table-responsive">
                                <table className="table table-striped align-middle">
                                    <thead>
                                        <tr>
                                            <th>Nombre</th>
                                            <th>Tipo</th>
                                            <th>Estado</th>
                                            <th className="text-end">Acciones</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {templates.length === 0 && (
                                            <tr>
                                                <td colSpan="4" className="text-center text-muted py-4">
                                                    Sin templates creados.
                                                </td>
                                            </tr>
                                        )}
                                        {templates.map((template) => (
                                            <tr key={template.id}>
                                                <td>
                                                    <div className="fw-bold">{template.nombre}</div>
                                                    <div className="small text-muted text-truncate" style={{ maxWidth: 460 }}>
                                                        {template.asunto ? `${template.asunto} · ` : ""}{template.cuerpo}
                                                    </div>
                                                </td>
                                                <td>
                                                    {(() => {
                                                        const tipo = TIPOS_CORREO_COBRANZA.find((t) => t.clave === template.tipo);
                                                        return tipo
                                                            ? <span className={`badge bg-${tipo.color} ${tipo.color === "warning" ? "text-dark" : ""}`}>{tipo.label}</span>
                                                            : <span className="text-muted small">—</span>;
                                                    })()}
                                                </td>
                                                <td>
                                                    <span className={`badge ${Number(template.activo) === 1 ? "bg-success" : "bg-secondary"}`}>
                                                        {Number(template.activo) === 1 ? "Activo" : "Inactivo"}
                                                    </span>
                                                </td>
                                                <td className="text-end">
                                                    <button
                                                        type="button"
                                                        className="btn btn-primary btn-sm me-2"
                                                        onClick={() => editarTemplate(template)}
                                                        disabled={loading}
                                                    >
                                                        Editar
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="btn btn-outline-danger btn-sm"
                                                        onClick={() => eliminarTemplate(template)}
                                                        disabled={loading}
                                                    >
                                                        Eliminar
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CorreoTemplates;
